import { useState } from "react";
import { TrendingUp, Wallet, Users, Inbox, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { euro } from "./format";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const keyOf = (iso) => monthKey(new Date(iso));
const labelOf = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};
const prevKey = (key) => {
  const [y, m] = key.split("-").map(Number);
  return monthKey(new Date(y, m - 2, 1));
};
const lastMonths = (n) => {
  const now = new Date();
  return Array.from({ length: n }, (_, i) => monthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
};

const sumPaid = (payments, filter = () => true) =>
  payments.filter((p) => p.payment_status === "paid" && filter(p)).reduce((acc, p) => acc + (p.amount || 0), 0);

const Delta = ({ current, previous }) => {
  if (previous === 0 && current === 0) return <span className="inline-flex items-center gap-1 text-white/60"><Minus size={11} /> stable</span>;
  if (previous === 0) return <span className="inline-flex items-center gap-1 text-gold-light"><ArrowUpRight size={11} /> nouveau</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 ${up ? "text-gold-light" : "text-red-300"}`} data-testid="admin-stat-month-delta">
      {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />} {up ? "+" : ""}{pct}% vs mois précédent
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, hint, testId, accent, children }) => (
  <div data-testid={testId} className={`rounded-3xl border p-5 ${accent ? "border-gold/40 bg-navy text-white" : "border-navy/10 bg-white text-navy"}`}>
    <div className="flex items-center justify-between">
      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${accent ? "text-gold-light" : "text-slate-500"}`}>{label}</span>
      <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${accent ? "bg-gold/20 text-gold" : "bg-gold/15 text-gold-dark"}`}>
        <Icon size={15} />
      </span>
    </div>
    <p className="mt-3 font-display text-2xl font-bold tracking-tight" data-testid={`${testId}-value`}>{value}</p>
    <p className={`mt-1 text-xs ${accent ? "text-white/60" : "text-slate-500"}`}>{hint}</p>
    {children}
  </div>
);

export const StatsBar = ({ payments, subs, leads, requests }) => {
  const months = lastMonths(12);
  const [month, setMonth] = useState(months[0]);
  const monthRevenue = sumPaid(payments, (p) => keyOf(p.created_at) === month);
  const prevRevenue = sumPaid(payments, (p) => keyOf(p.created_at) === prevKey(month));
  const totalRevenue = sumPaid(payments);
  const active = subs.filter((s) => !s.cancel_at_period_end);
  const activeSubs = active.length;
  const mrr = active.reduce((acc, s) => acc + (s.interval === "year" ? (s.amount || 0) / 12 : s.amount || 0), 0);
  const pending = leads.filter((l) => !["signee", "en_construction", "publie", "perdue"].includes(l.status)).length + requests.filter((r) => r.status !== "traitee").length;

  return (
    <section className="mt-8" data-testid="admin-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-navy">Vue d'ensemble</h2>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          Période
          <select
            data-testid="admin-stats-month-select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-full border border-navy/15 bg-white px-4 py-2 text-xs font-semibold capitalize text-navy outline-none transition-colors focus:border-gold"
          >
            {months.map((k) => <option key={k} value={k}>{labelOf(k)}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard accent icon={TrendingUp} label="Revenus du mois" value={euro(monthRevenue)} hint={<span className="capitalize">{labelOf(month)}</span>} testId="admin-stat-month-revenue">
          <p className="mt-2 text-[11px]"><Delta current={monthRevenue} previous={prevRevenue} /></p>
        </StatCard>
        <StatCard icon={Wallet} label="Revenus totaux" value={euro(totalRevenue)} hint={`${payments.filter((p) => p.payment_status === "paid").length} paiement(s) encaissé(s)`} testId="admin-stat-total-revenue" />
        <StatCard icon={Users} label="Abonnés actifs" value={String(activeSubs)} hint={`${euro(Math.round(mrr))} / mois récurrents`} testId="admin-stat-active-subs" />
        <StatCard icon={Inbox} label="À traiter" value={String(pending)} hint="demandes + modifications en attente" testId="admin-stat-pending" />
      </div>
    </section>
  );
};
